import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Lead, LeadStatus, Task } from '@/types/lead';
import { getLeadById, toggleFavorite, addDocument } from '@/services/leads';
import { updateLeadStatus } from '@/services/leads/leadStatusService';
import { logActivity } from '@/services/leads/activityService';
import { addNote } from '@/services/leads/noteService';
import { refreshPlaceData } from '@/services/places/refreshService';
import { getTasksByLead } from '@/services/leads/taskService';
import { useAuth } from '@/context/AuthContext';

export function useBusinessData(id: string | undefined) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useAuth();
  const [lead, setLead] = useState<Lead | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchLead = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      // Fetch lead and tasks in parallel for TaskNotifications optimization
      const [leadData, tasksData] = await Promise.all([
        getLeadById(id),
        getTasksByLead(id)
      ]);
      
      if (leadData) {
        setLead(leadData);
        setTasks(tasksData);
      } else {
        // Silently navigate - lead may have been deleted
        navigate('/app');
      }
    } catch (error) {
      console.error('Error fetching lead:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load business details"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleToggleFavorite = async () => {
    if (!lead) return;
    try {
      const updatedLead = await toggleFavorite(lead.id);
      setLead(prev => prev ? {
        ...prev,
        isFavorite: updatedLead.isFavorite
      } : null);
      await logActivity({
        leadId: lead.id,
        type: 'other',
        title: updatedLead.isFavorite ? 'Marked as favorite' : 'Removed from favorites'
      });
      toast({
        title: updatedLead.isFavorite ? "Added to Favorites" : "Removed from Favorites",
        description: `${lead.name} has been ${updatedLead.isFavorite ? 'added to' : 'removed from'} your favorites.`
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update favorite status"
      });
    }
  };

  const handleStatusChange = async (newStatus: LeadStatus, notes?: string) => {
    if (!lead) return;
    try {
      const success = await updateLeadStatus(lead.id, newStatus, notes);
      
      if (success) {
        setLead(prev => prev ? {
          ...prev,
          status: newStatus
        } : null);
        
        toast({
          title: "Status Updated",
          description: `${lead.name} has been moved to ${newStatus} status.`
        });
      }
    } catch (error) {
      console.error('Error updating lead status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update lead status"
      });
    }
  };

  const handleAddNote = async (content: string, tags?: string[]) => {
    if (!lead) return;
    try {
      const newNote = await addNote(lead.id, content, tags);
      setLead(prev => prev ? {
        ...prev,
        notes: [...prev.notes, newNote]
      } : null);
      await logActivity({
        leadId: lead.id,
        type: 'note',
        title: 'Note added',
        description: content
      });
      toast({
        title: "Note Added",
        description: "Your note has been added successfully"
      });
    } catch (error) {
      console.error('Error adding note:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add note"
      });
    }
  };

  const handleAddDocument = async (name: string, url: string, type: string, description?: string) => {
    if (!lead) return;
    try {
      const newDocument = await addDocument(lead.id, name, url, type, description);
      
      setLead(prev => prev ? {
        ...prev,
        documents: [...prev.documents, newDocument]
      } : null);
      
      await logActivity({
        leadId: lead.id,
        type: 'document',
        title: `Document added: ${name}`,
        metadata: {
          documentType: type,
          hasDescription: !!description
        }
      });
      
      toast({
        title: "Document Added",
        description: "Your document has been added successfully"
      });
      
      return newDocument;
    } catch (error) {
      console.error('Error adding document:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add document"
      });
      throw error; // Rethrow the error so the UI can handle it
    }
  };

  const handleRefreshPlaceData = async () => {
    if (!lead) return;
    
    setIsRefreshing(true);
    try {
      const refreshedLead = await refreshPlaceData(lead);
      if (refreshedLead) {
        setLead(refreshedLead);
        toast({
          title: "Refresh Successful",
          description: "Business information has been updated",
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    lead,
    tasks,
    isLoading,
    activeTab,
    setActiveTab,
    handleToggleFavorite,
    handleStatusChange,
    handleAddNote,
    handleAddDocument,
    fetchLead,
    handleRefreshPlaceData,
    isRefreshing
  };
}
